import { BuildType } from 'mbs-ui-shared@models/BuildType.enum';
import { AgentType } from 'mbs-ui-shared@models/Computer';
import { AgentAvailableBuildVersions } from 'mbs-ui-shared@models/ComputersModals';
import { Build, BuildOsType, getNewBuildSection, getReleaseNotesPath } from 'mbs-ui-shared@models/build';
import { versionCompare } from 'mbs-ui-shared@utils/version-compare';

export enum BuildAction {
  GenerateFirst = 'generateFirst',
  MakePublic = 'makePublic',
  GenerateSandbox = 'generateSandbox',
  Generate = 'generate'
}

export type NewVersionEntry = {
  buildType: BuildType;
  os: BuildOsType;
  version?: string;
  releaseNotesPath?: string;
  currentVersion?: string;
  buildName?: string;
};

export type BuildActionOffer = {
  action: BuildAction;
  buildTypes: BuildType[];
  entries: NewVersionEntry[];
  publicVersion?: string;
  showOs?: boolean;
};

type OfferedVersion = {
  build: Build;
  version: string;
  currentVersion: string;
};

export const isSandboxSupported = (applicationId: string): boolean => applicationId !== AgentType.RA;

const isSandboxOn = (applicationId: string, isSandBoxAvailable: boolean): boolean =>
  isSandBoxAvailable && isSandboxSupported(applicationId);

const getGenerateAction = (sandboxOn: boolean): BuildAction => (sandboxOn ? BuildAction.GenerateSandbox : BuildAction.Generate);

const getReplacedVersion = (build: Build, sandboxOn: boolean): string => (sandboxOn ? build.sandbox?.version : build.public?.version);

const getNewerVersion = (build: Build, sandboxOn: boolean): string => {
  const section = getNewBuildSection(build, sandboxOn);

  return section?.isNewerVersionAvailable ? section.availableVersion || null : null;
};

const toEntry = ({ build, version, currentVersion }: OfferedVersion): NewVersionEntry => ({
  version,
  buildType: build.mbsBuildType,
  os: build.buildOs,
  releaseNotesPath: getReleaseNotesPath(build.applicationId, build.buildOs, version),
  currentVersion
});

const getFirstPublicOffer = (build: Build, sandboxOn: boolean): BuildActionOffer =>
  !build.public && (!sandboxOn || !build.sandbox)
    ? {
        action: BuildAction.GenerateFirst,
        buildTypes: [build.mbsBuildType],
        entries: [{ buildType: build.mbsBuildType, os: build.buildOs, buildName: build.name }]
      }
    : null;

const getSingleMakePublicOffer = (build: Build, sandboxOn: boolean): BuildActionOffer =>
  sandboxOn && build.sandbox
    ? {
        action: BuildAction.MakePublic,
        buildTypes: [build.mbsBuildType],
        entries: [toEntry({ build, version: build.sandbox.version, currentVersion: build.public?.version })],
        publicVersion: build.public?.version
      }
    : null;

const getSingleNewVersionOffer = (agent: AgentAvailableBuildVersions, build: Build, sandboxOn: boolean): BuildActionOffer => {
  const version = getNewerVersion(build, sandboxOn);

  if (!version || versionCompare(version, agent.currentVersion) <= 0) return null;

  return {
    action: getGenerateAction(sandboxOn),
    buildTypes: [build.mbsBuildType],
    entries: [toEntry({ build, version, currentVersion: getReplacedVersion(build, sandboxOn) })],
    publicVersion: build.public?.version
  };
};

const toGroupOffer = (action: BuildAction, offeredVersions: OfferedVersion[]): BuildActionOffer =>
  offeredVersions.length
    ? {
        action,
        buildTypes: offeredVersions.map(({ build }) => build.mbsBuildType),
        entries: offeredVersions.map(toEntry),
        showOs: true
      }
    : null;

export function getSingleBuildOffers(agent: AgentAvailableBuildVersions, build: Build, isSandBoxAvailable: boolean): BuildActionOffer[] {
  if (!agent || !build) return [];

  const sandboxOn = isSandboxOn(build.applicationId, isSandBoxAvailable);

  return [
    getFirstPublicOffer(build, sandboxOn),
    getSingleMakePublicOffer(build, sandboxOn),
    getSingleNewVersionOffer(agent, build, sandboxOn)
  ].filter(Boolean);
}

export function getGroupBuildOffers(applicationId: string, builds: Build[], isSandBoxAvailable: boolean): BuildActionOffer[] {
  const sandboxOn = isSandboxOn(applicationId, isSandBoxAvailable);
  const productBuilds = (builds ?? []).filter((build) => build.applicationId === applicationId);
  const sandboxVersions = sandboxOn
    ? productBuilds
        .filter((build) => !!build.sandbox)
        .map((build) => ({ build, version: build.sandbox.version, currentVersion: build.public?.version }))
    : [];
  const newerVersions = productBuilds
    .map((build) => ({ build, version: getNewerVersion(build, sandboxOn), currentVersion: getReplacedVersion(build, sandboxOn) }))
    .filter(({ version }) => !!version);

  return [toGroupOffer(BuildAction.MakePublic, sandboxVersions), toGroupOffer(getGenerateAction(sandboxOn), newerVersions)].filter(Boolean);
}
